import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { MissionCollectionCard } from './MissionCollectionCard/index.tsx';
import type {
  TaskCollectionsQuery,
  TaskCollectionsQueryVariables,
  TaskCollections_taskInterface,
} from './__generated__/index.graphql.ts';

const TASK_COLLECTIONS_QUERY = gql`
  query TaskCollectionsQuery(
    $taskId: ID!
    $user: String!
    $after: String
    $pageSize: Int
  ) {
    currentUser {
      slug
      task(id: $taskId, bypassRebuildCollectionTask: true) {
        id
        ... on CompleteCollectionsCountTargetTask {
          id
          cardCollectionsWithProgress(first: $pageSize, after: $after) {
            nodes {
              ...MissionCollectionCard_cardCollectionWithProgress
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      }
    }
  }
  ${MissionCollectionCard.fragments.cardCollectionWithProgress}
` as TypedDocumentNode<TaskCollectionsQuery, TaskCollectionsQueryVariables>;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  gap: var(--unit);
`;

type Props = {
  task: TaskCollections_taskInterface;
};

const PAGE_SIZE = 6;

export const TaskCollections = ({ task }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const isCollectionsTask = isType(task, 'CompleteCollectionsCountTargetTask');

  const { data, loadMore, networkStatus } = usePaginatedQuery(
    TASK_COLLECTIONS_QUERY,
    {
      variables: {
        taskId: idFromObject(task.id),
        user: currentUser?.slug ?? '',
        pageSize: PAGE_SIZE,
      },
      connection: 'CardCollectionWithProgressConnection',
      skip: !isCollectionsTask,
      notifyOnNetworkStatusChange: true,
    }
  );

  if (!isCollectionsTask) {
    return null;
  }

  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const taskSetColor = 'var(--c-nd-600-opaque)';

  const paginatedTask = data?.currentUser?.task;
  const { nodes, pageInfo } =
    (isType(paginatedTask, 'CompleteCollectionsCountTargetTask') &&
      paginatedTask.cardCollectionsWithProgress) ||
    {};

  return (
    <Vertical>
      <Horizontal>
        <div style={{ color: taskSetColor }}>
          <Collection size={2} />
        </div>
        <BrandS>
          <FormattedMessage defaultMessage="My collection" />
        </BrandS>
      </Horizontal>
      <Grid>
        {nodes?.map(node => (
          <MissionCollectionCard
            key={node.cardCollection.slug}
            cardCollectionWithProgress={node}
            strokeColor={taskSetColor}
          />
        ))}
      </Grid>
      {pageInfo?.hasNextPage ? (
        <Button
          size="medium"
          color="quaternary"
          loading={loadingMore}
          onClick={() => {
            loadMore(false, { after: pageInfo.endCursor });
          }}
        >
          <FormattedMessage {...glossary.viewMore} />
        </Button>
      ) : null}
    </Vertical>
  );
};

TaskCollections.fragments = {
  taskInterface: gql`
    fragment TaskCollections_taskInterface on TaskInterface {
      id
      ... on CompleteCollectionsCountTargetTask {
        id
      }
    }
  ` as TypedDocumentNode<TaskCollections_taskInterface>,
};
